import { useNavigate } from 'react-router-dom';

import type { EventView } from '@ndvr/customer-api';

import { useDockState } from '@/views/dock/dock-state';
import { convertBackendTimeToBrowser, formatDate, formatTime } from 'utils';
import { useListTasksByHouseholdQuery } from 'views/tasks/task-queries';
import { useTaskOverlayState } from 'views/tasks/task-state';

import { useNotesByEventQuery, useNotesQuery } from '../HouseholdNotes/note-queries';
import {
    EventMeetingBot,
    EventNoteView,
    EventTaskView,
    NON_JOINED_MEETING_BOT_STATUSES,
    SCHEDULED_MEETING_BOT_STATUSES,
} from './event-details-shared';
import { useGetMeetingBot, useInviteMeetingBot, useRemoveMeetingBot, useScheduleMeetingBot } from './events-queries';

export interface UseEventDetailsModelOptions {
    event: EventView;
    meetingBotEnabled: boolean;
    onRequestClose?: () => void;
}

export interface EventDetailsActionFlags {
    canInviteMeetingBot: boolean;
    canInviteMeetingBotDisabledReason: string | null;
    canScheduleMeetingBot: boolean;
    canJoinMeeting: boolean;
    canStopMeetingBot: boolean;
    canUnscheduleMeetingBot: boolean;
}

export interface EventDetailsActionHandlers {
    onCreateNote: () => void;
    onCreateTask: () => void;
    onInviteMeetingBot: () => void;
    onScheduleMeetingBot: () => void;
    onRemoveMeetingBot: () => void;
}

export interface EventDetailsActionProps extends EventDetailsActionFlags, EventDetailsActionHandlers {
    eventUrl?: string;
}

export interface EventDetailsModel extends EventDetailsActionFlags, EventDetailsActionHandlers {
    eventDate: string;
    eventTimeRange: string;
    meetingBot: EventMeetingBot | null;
    notes: EventNoteView[];
    onNoteClick: (note: EventNoteView) => void;
    onTaskClick: (taskGuid: string) => void;
    tasks: EventTaskView[];
}

export function useEventDetailsModel({
    event,
    meetingBotEnabled,
    onRequestClose,
}: UseEventDetailsModelOptions): EventDetailsModel {
    const eventStartDate = convertBackendTimeToBrowser(event.startTime, event.startTimeZone);
    const eventEndDate = convertBackendTimeToBrowser(event.endTime, event.endTimeZone);
    const today = new Date();
    const isPastEvent = eventEndDate < today;
    const isToday =
        eventStartDate.getFullYear() === today.getFullYear() &&
        eventStartDate.getMonth() === today.getMonth() &&
        eventStartDate.getDate() === today.getDate();
    const eventDate = formatDate(eventStartDate);
    const eventTimeRange = `${formatTime(eventStartDate)} - ${formatTime(eventEndDate)}`;
    const householdNotesQuery = useNotesQuery(event.clientHouseholdGUID, event.eventGUID);
    const eventNotesQuery = useNotesByEventQuery(!event.clientHouseholdGUID ? event.eventGUID : undefined);
    const notesViewQuery = event.clientHouseholdGUID ? householdNotesQuery : eventNotesQuery;
    const tasksQuery = useListTasksByHouseholdQuery(event.clientHouseholdGUID, event.eventGUID);
    const meetingBotQuery = useGetMeetingBot(event.clientHouseholdGUID, event.eventGUID, meetingBotEnabled);
    const inviteMeetingBotMutation = useInviteMeetingBot(event.eventGUID);
    const scheduleMeetingBotMutation = useScheduleMeetingBot(event.eventGUID);
    const removeMeetingBotMutation = useRemoveMeetingBot(event.eventGUID);
    const navigate = useNavigate();
    const { addCreateTaskDockItem, addCreateNoteDockItem } = useDockState();
    const { openViewTaskSheet } = useTaskOverlayState();

    const tasks = tasksQuery.data ?? [];
    const meetingBot = meetingBotQuery.data ?? null;
    const isMeetingBotJoined = Boolean(meetingBot && !NON_JOINED_MEETING_BOT_STATUSES.includes(meetingBot.status));
    const isMeetingBotScheduled = Boolean(meetingBot && SCHEDULED_MEETING_BOT_STATUSES.includes(meetingBot.status));

    function handleInviteMeetingBot(): void {
        inviteMeetingBotMutation.mutate();
    }

    function handleScheduleMeetingBot(): void {
        scheduleMeetingBotMutation.mutate();
    }

    function handleRemoveMeetingBot(): void {
        removeMeetingBotMutation.mutate();
    }

    function handleCreateNote(): void {
        onRequestClose?.();
        addCreateNoteDockItem(event.clientHouseholdGUID, event.eventGUID);
    }

    function handleCreateTask(): void {
        onRequestClose?.();
        addCreateTaskDockItem(event.clientHouseholdGUID, undefined, event.eventGUID);
    }

    function handleNoteClick(note: EventNoteView): void {
        if (note.householdGUID) {
            navigate(`/household/${note.householdGUID}/notes/view?noteGUID=${note.noteGUID}`);
        } else {
            navigate(`/notes/view?noteGUID=${note.noteGUID}`);
        }
    }

    function handleTaskClick(taskGuid: string): void {
        openViewTaskSheet(taskGuid);
    }

    const isMeetingBotActive = Boolean(meetingBot);
    const canInviteMeetingBotDisabledReason: string | null = (() => {
        if (isMeetingBotJoined) return null;
        if (isPastEvent) return 'This meeting has already ended.';
        if (isToday && event.url == null) return 'A meeting URL is required to invite Claire™.';
        return null;
    })();

    return {
        canInviteMeetingBot: isToday && !isPastEvent && !isMeetingBotJoined && event.url != null,
        canInviteMeetingBotDisabledReason,
        canScheduleMeetingBot: !isToday && !isPastEvent && !isMeetingBotActive,
        canJoinMeeting: !isPastEvent && Boolean(event.url),
        canStopMeetingBot: isMeetingBotJoined,
        canUnscheduleMeetingBot: isMeetingBotScheduled,
        eventDate,
        eventTimeRange,
        meetingBot,
        notes: notesViewQuery.data ?? [],
        onCreateNote: handleCreateNote,
        onCreateTask: handleCreateTask,
        onInviteMeetingBot: handleInviteMeetingBot,
        onScheduleMeetingBot: handleScheduleMeetingBot,
        onNoteClick: handleNoteClick,
        onRemoveMeetingBot: handleRemoveMeetingBot,
        onTaskClick: handleTaskClick,
        tasks,
    };
}
